import React, { useEffect, useState, useCallback } from "react";
import useAPI from "../../Hooks/useAPI";
import "./UserPermissionManager.css";
import { default as Footer } from "../../Components/Footer/FooterSmall";
import "../../Components/Page/Page.css";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

type User = { id: string; email: string };
type Permission = { id: string; key: string; description: string };

export const UserPermissionManager: React.FC = () => {
  const navigate = useNavigate();
  const {
    getUsers,
    getPermissions,
    getUserPermissions,
    assignPermissionToUser,
    removePermissionFromUser,
    getUserById,
  } = useAPI();

  const [users, setUsers] = useState<User[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [assignedPermissionIds, setAssignedPermissionIds] = useState<
    Set<string>
  >(new Set());
  const [selectedUserRole, setSelectedUserRole] = useState<string | null>(null);

  const loadInitialData = useCallback(async () => {
    const userRes = await getUsers("");
    const permRes = await getPermissions();
    if (userRes?.data) setUsers(userRes.data);
    if (permRes) setPermissions(permRes);
  }, [getUsers, getPermissions]);

  const loadUserPermissions = useCallback(
    async (userId: string) => {
      const assigned = await getUserPermissions(userId);
      if (Array.isArray(assigned)) {
        const ids = new Set(assigned.map((p: any) => p.id || p));
        setAssignedPermissionIds(ids);
      }
    },
    [getUserPermissions]
  );

  const loadUserRole = useCallback(
    async (userId: string) => {
      const userResponse = await getUserById(userId);
      if (userResponse?.data) {
        setSelectedUserRole(userResponse.data.role);
      }
    },
    [getUserById]
  );

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (selectedUserId) {
      loadUserPermissions(selectedUserId);
      loadUserRole(selectedUserId);
    } else {
      setAssignedPermissionIds(new Set());
      setSelectedUserRole(null);
    }
  }, [selectedUserId, loadUserPermissions, loadUserRole]);

  const togglePermission = async (permissionId: string) => {
    if (!selectedUserId) return;
    const hasPermission = assignedPermissionIds.has(permissionId);

    if (hasPermission) {
      await removePermissionFromUser(selectedUserId, permissionId);
      setAssignedPermissionIds((prev) => {
        const next = new Set(prev);
        next.delete(permissionId);
        return next;
      });
    } else {
      await assignPermissionToUser(selectedUserId, permissionId);
      setAssignedPermissionIds((prev) => new Set(prev).add(permissionId));
    }
  };

  return (
    <div className="generalPage">
      <div className="pageContainer">
        {/*   <Header /> */}
        <div className="button-container">
          <button
            className="back-button"
            onClick={() => navigate("/user-admin")}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
          </button>
        </div>
        <div className="user-permission-manager">
          <h2 className="user-permission-manager__title">
            Assign Permissions to User
          </h2>

          <select
            value={selectedUserId || ""}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="user-permission-manager__select"
          >
            <option value="">Select a user</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.email}
              </option>
            ))}
          </select>

          {selectedUserId && (
            <div className="user-permission-manager__permissions">
              {selectedUserRole && (
                <div className="user-permission-manager__role-info">
                  <h3 className="user-permission-manager__role-title">
                    User Role
                  </h3>
                  <p className="user-permission-manager__role">
                    {selectedUserRole}
                  </p>
                </div>
              )}
              <h3 className="user-permission-manager__permissions-title">
                Direct Permissions
              </h3>
              <ul className="user-permission-manager__permissions-list">
                {permissions.map((perm) => (
                  <li
                    key={perm.id}
                    className="user-permission-manager__permission-item"
                  >
                    <label className="user-permission-manager__permission-label">
                      <input
                        type="checkbox"
                        checked={assignedPermissionIds.has(perm.id)}
                        onChange={() => togglePermission(perm.id)}
                        className="user-permission-manager__checkbox"
                      />
                      <span>
                        <strong>{perm.key}</strong>: {perm.description}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};
